// src/routes/router.ts
import {
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  redirect,
} from '@tanstack/react-router';

import AuthLayout from '@/layout/AuthLayout/AuthLayout';
import Register from '@/pages/Register/Register';
import Login from '@/pages/Login/Login';
import ResetPassword from '@/pages/ResetPassword/ResetPassword';
import ForgotPassword from '@/pages/ForgotPassword/ForgotPassword';
import AdminDashboardLayout from '@/layout/AdminDashboardLayout/AdminDashboardLayout';
import AccountOverview from '@/pages/AccountOverview/AccountOverview';
import Administrators from '@/pages/Administrators/Administrators';
import Users from '@/pages/Users/Users';
import { getSessionToken } from '@/utils/sessionStorage';
import ActivityLog from '@/pages/ActivityLog/ActivityLog';
import RecoveryVerifyEmailOtpPage from '@/pages/PasswordRecovery/RecoveryVerifyEmailOtp';
import ForcedResetOtpPage from '@/pages/PasswordRecovery/ForcedResetOtp';
import RecoveryVerifyPhoneOtp from '@/pages/PasswordRecovery/RecoveryVerifyPhoneOtp';
import RecoverySetPasswordPage from '@/pages/PasswordRecovery/RecoverySetPassword';

// Root outlet
const rootRoute = createRootRoute({ component: Outlet });

// ---------- Auth ----------
const authLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'auth',
  component: AuthLayout,
});

const registrationRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/registration',
  component: Register,
});

const loginRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/login',
  component: Login,
  loader: () => {
    const token = getSessionToken();
    if (token !== null) {
      return redirect({ to: '/admin' });
    }
    return null;
  },
});

const forgotPasswordRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/forgot-password',
  component: ResetPassword,
});

const resetPasswordRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/reset-password',
  component: ForgotPassword,
});

const recoverPasswordOtpRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/reset-password/otp-verify',
  component: RecoveryVerifyEmailOtpPage,
});

const forcedResetOtpRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/reset-password/forced',
  component: ForcedResetOtpPage,
});

const recoverPasswordTwoFactorRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/reset-password/two-factor-verify',
  component: RecoveryVerifyPhoneOtp,
});

const recoverSetPasswordRoute = createRoute({
  getParentRoute: () => authLayoutRoute,
  path: '/reset-password/set-password',
  component: RecoverySetPasswordPage,
});

// ---------- Admin ----------
const adminLayoutRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/admin',
  component: AdminDashboardLayout,
  beforeLoad: () => {
    const token = getSessionToken();
    if (token == null) {
      return redirect({ to: '/login' }); // no token → back to login
    }
    return null;
  },
});

// /admin → /admin/account
const adminIndexRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: '/',
  loader: () => redirect({ to: '/admin/account' }),
});

const adminAccountRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'account',
  component: AccountOverview,
});

const adminAdministratorsRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'administrators',
  component: Administrators,
});

const adminUsersRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'users',
  component: Users,
});

const adminActivityLogRoute = createRoute({
  getParentRoute: () => adminLayoutRoute,
  path: 'activity-log',
  component: ActivityLog,
});

// ---------- Root "/" ----------
// If token: go straight to /admin (which redirects to /admin/account)
// Else: go to /login
const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  loader: () => {
    const token = getSessionToken();
    return token !== null ? redirect({ to: '/admin' }) : redirect({ to: '/login' });
  },
});

// Route tree
const routeTree = rootRoute.addChildren([
  authLayoutRoute.addChildren([
    registrationRoute,
    loginRoute,
    forgotPasswordRoute,
    resetPasswordRoute,
    recoverPasswordOtpRoute,
    forcedResetOtpRoute,
    recoverPasswordTwoFactorRoute,
    recoverSetPasswordRoute,
  ]),
  indexRoute,
  adminLayoutRoute.addChildren([
    adminIndexRoute,
    adminAccountRoute,
    adminAdministratorsRoute,
    adminUsersRoute,
    adminActivityLogRoute,
  ]),
]);

export const router = createRouter({ routeTree });

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
